import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { User, Mail, Lock, CheckCircle2, ArrowRight, ShieldCheck, Globe, Loader2, Cpu, Phone, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Register() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone_number: "",
    telegram_username: "",
    password: "",
    confirmPassword: "",
    referralCode: new URLSearchParams(window.location.search).get('ref') || ""
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async () => {
    const { username, email, phone_number, password, confirmPassword } = formData;
    
    if (!username || !email || !phone_number || !password) {
      setError(t('auth.fill_all_fields'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwords_dont_match'));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await register(formData);
      if (result.success) {
        if (result.requires_verification) {
          navigate("/verify-email", { state: { email: formData.email } });
        } else {
          setSuccess(true);
          setTimeout(() => navigate("/login"), 3000);
        }
      } else {
        setError(result.error || t('auth.invalid_credentials'));
      }
    } catch (err) {
      setError(t('auth.tech_error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 font-inter p-6 text-center">
        <div className="glass-card p-12 max-w-md border-indigo-500/30 animate-fade-in shadow-indigo-500/20">
          <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-emerald-500/20">
            <CheckCircle2 size={40} className="text-emerald-500" />
          </div>
          <h2 className="text-3xl font-black text-white font-outfit mb-4 uppercase tracking-tight">{t('auth.account_initialized')}</h2>
          <p className="text-slate-400 font-medium mb-8 leading-relaxed">
            {t('auth.profile_created')}
          </p>
          <div className="flex justify-center">
            <Loader2 size={24} className="animate-spin text-indigo-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden px-4 font-inter py-20">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-full max-w-[800px] h-[800px] bg-indigo-500/5 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-full max-w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="w-full max-w-full max-w-[550px] z-10 animate-fade-in">
        {/* Branding & Header */}
        <div className="flex flex-col items-center mb-10">
          <Link to="/" className="mb-6 flex items-center gap-3 glass-card px-4 py-2 hover:border-indigo-500/30 transition-all border-none shadow-none">
            <Cpu size={24} className="text-indigo-400" />
            <span className="text-xl font-black bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent uppercase">Ennovatrade</span>
          </Link>
          <h1 className="text-4xl font-black text-white font-outfit mb-2 tracking-tight uppercase">{t('auth.open_pro_account')}</h1>
          <p className="text-slate-400 font-medium text-center">{t('auth.access_next_gen')}</p>
        </div>

        {/* Register Card */}
        <div className="glass-card p-10 border-white/10 shadow-2xl relative bg-slate-900/60 backdrop-blur-2xl">
          {error && (
            <div className="mb-8 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center gap-3 text-rose-400 animate-fade-in font-semibold text-sm">
              <div className="w-2 h-2 rounded-full bg-rose-500"></div>
              {error}
            </div>
          )}

          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Username Field */}
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.unique_id')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-600 group-focus-within:text-indigo-400 transition-colors">
                    <User size={18} />
                  </div>
                  <input 
                    name="username"
                    value={formData.username} 
                    onChange={handleChange}
                    placeholder="Trader_Nova"
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all font-medium"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Referral Field */}
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.referral_code')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-600">
                    <Globe size={18} />
                  </div>
                  <input 
                    name="referralCode"
                    value={formData.referralCode} 
                    onChange={handleChange}
                    placeholder="XYZ-123"
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all font-medium"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.email_pro')}</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-600 group-focus-within:text-indigo-400 transition-colors">
                  <Mail size={18} />
                </div>
                <input 
                  name="email"
                  type="email" 
                  value={formData.email} 
                  onChange={handleChange}
                  placeholder={t('auth.email_placeholder')}
                  className="w-full pl-11 pr-4 py-4 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all font-medium"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Phone Number Field */}
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.phone_number')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-600 group-focus-within:text-indigo-400 transition-colors">
                    <Phone size={18} />
                  </div>
                  <input 
                    name="phone_number"
                    type="tel" 
                    value={formData.phone_number} 
                    onChange={handleChange}
                    placeholder="+1 234 567 890"
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all font-medium"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              {/* Telegram Field */}
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.telegram_username')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-600 group-focus-within:text-indigo-400 transition-colors">
                    <MessageSquare size={18} />
                  </div>
                  <input 
                    name="telegram_username"
                    type="text" 
                    value={formData.telegram_username} 
                    onChange={handleChange}
                    placeholder="@votre_nom"
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all font-medium"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
              {/* Password Field */}
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.password')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-600 group-focus-within:text-indigo-400 transition-colors">
                    <Lock size={18} />
                  </div>
                  <input 
                    name="password"
                    type="password" 
                    value={formData.password} 
                    onChange={handleChange}
                    placeholder="••••••••••••"
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all font-medium"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.confirm_password')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-600 group-focus-within:text-indigo-400 transition-colors">
                    <ShieldCheck size={18} />
                  </div>
                  <input 
                    name="confirmPassword"
                    type="password" 
                    value={formData.confirmPassword} 
                    onChange={handleChange}
                    placeholder="••••••••••••"
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all font-medium"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Compliance Checkbox */}
            <div className="flex items-start gap-3 py-4">
              <input 
                type="checkbox" 
                id="tos" 
                className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
                required
              />
              <label htmlFor="tos" className="text-xs text-slate-500 font-bold leading-relaxed tracking-wide">
                {t('auth.tos_accept')} <span className="text-indigo-400 cursor-pointer">{t('auth.tos')}</span> {t('auth.and')} <span className="text-indigo-400 cursor-pointer">{t('auth.privacy_policy')}</span> {t('auth.of_platform')}
              </label>
            </div>

            {/* Submit Button */}
            <button 
              onClick={handleRegister} 
              disabled={loading}
              className="btn-premium w-full !py-4.5 text-white uppercase tracking-widest font-black text-sm flex items-center justify-center gap-3 disabled:opacity-50 group hover:shadow-indigo-500/40"
            >
              {loading ? (
                <Loader2 size={24} className="animate-spin" />
              ) : (
                <>
                  {t('auth.create_access')}
                  <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </div>

          <div className="mt-10 py-5 border-t border-white/5 flex flex-col items-center gap-4">
            <p className="text-sm font-bold text-slate-500 tracking-wide uppercase">
              {t('auth.already_member')}{" "}
              <Link 
                to="/login" 
                className="text-indigo-400 hover:text-indigo-300 transition-colors"
                disabled={loading}
              >
                {t('auth.login')}
              </Link>
            </p>
          </div>
        </div>

        {/* Features Minimal Grid */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 opacity-60">
            <div className="flex flex-col items-center gap-2">
                <CheckCircle2 size={20} className="text-indigo-500" />
                <span className="text-[10px] font-black text-slate-500 text-center uppercase tracking-widest leading-none">{t('auth.trading_pro')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
                <CheckCircle2 size={20} className="text-indigo-500" />
                <span className="text-[10px] font-black text-slate-500 text-center uppercase tracking-widest leading-none">{t('auth.packs_roi')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
                <CheckCircle2 size={20} className="text-indigo-500" />
                <span className="text-[10px] font-black text-slate-500 text-center uppercase tracking-widest leading-none">{t('auth.mlm_unilevel')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
                <CheckCircle2 size={20} className="text-indigo-500" />
                <span className="text-[10px] font-black text-slate-500 text-center uppercase tracking-widest leading-none">{t('auth.secured_data')}</span>
            </div>
        </div>
      </div>
    </div>
  );
}